import React, { useRef, createContext, useContext } from 'react';
import { useSelector } from 'react-redux';
import { useTranslate } from '@tolgee/react';
import { LomavisDemographicsSectionForPlatform1 } from '@/lomavisSharedUi/templates/sections/analytics/lomavisDemographicsSectionForPlatform1';
import { extractDemographicsData } from '@/redux/slices/analytics/demographics';
import {
  ALL,
  FACEBOOK,
  INSTAGRAM,
  LINKEDIN_BUSINESS_PAGE,
  SUPPORTED_PLATFORMS_DEMOGRAPHICS
} from '@/config/constants';
import IsLoadingIndicator from '@/components/isLoadingIndicator';
import { LomavisDataProcessingPlaceholder } from '@/lomavisSharedUi/atoms/placeholders/LomavisDataProcessingPlaceholder';
import { LomavisTitle1 } from '@/lomavisSharedUi/molecules/typography/titles/LomavisTitle1';
import { LomavisCard1 } from '@/lomavisSharedUi/atoms/cards/LomavisCard1';
import { LomavisPlatformIcon } from '@/lomavisSharedUi/atoms/icons/LomavisPlatformIcon';

// Create a context to share refs between components
const DemographicsRefsContext = createContext<Record<string, React.RefObject<HTMLDivElement>>>({});

const PlatformNavbar: React.FC = () => {
  const { platforms_information } = useSelector((state: any) => state.locationDetails);
  const { t } = useTranslate();
  const refs = useContext(DemographicsRefsContext);
  
  const activeSupportedPlatforms = SUPPORTED_PLATFORMS_DEMOGRAPHICS.filter(
    (platform) => platforms_information && Object.keys(platforms_information).includes(platform)
  );

  const handlePlatformClick = (platform: string) => {
    const ref = refs[platform];
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  if (activeSupportedPlatforms.length === 0) {
    return null;
  }

  // Include ALL in navigation if there are any active platforms
  const navigationPlatforms = activeSupportedPlatforms.length > 0 ? [...activeSupportedPlatforms] : [];

  return (
    <div className="d-flex justify-content-end mb-4">
      <div className="d-flex gap-3">
        {navigationPlatforms.map((platform) => (
          <button
            key={platform}
            onClick={() => handlePlatformClick(platform)}
            className="btn btn-white border d-flex align-items-center gap-2 px-3 py-2"
          >
            <LomavisPlatformIcon platform={platform} />
            <span>{t(`platforms.${platform}`)}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const DemographicsTab: React.FC = () => {
  const { data: demographicsData, isLoading: demographicsLoading } =
    useSelector((state: any) => state.demographics);
  const { platforms_information } = useSelector((state: any) => state.locationDetails);
  const { t } = useTranslate();

  // Define available platforms for demographics (including ALL for combined data)
  const availablePlatforms = [ALL, FACEBOOK, INSTAGRAM, LINKEDIN_BUSINESS_PAGE];

  // Create refs for each platform section
  const platformRefs = availablePlatforms.reduce((acc, platform) => {
    acc[platform] = useRef<HTMLDivElement>(null);
    return acc;
  }, {} as Record<string, React.RefObject<HTMLDivElement>>);

  // Check if there are any active platforms from the supported platforms
  const activeSupportedPlatforms = SUPPORTED_PLATFORMS_DEMOGRAPHICS.filter(
    (platform) => platforms_information && Object.keys(platforms_information).includes(platform)
  );

  const hasActiveSupportedPlatforms = activeSupportedPlatforms.length > 0;

  return (
    <DemographicsRefsContext.Provider value={platformRefs}>
      <PlatformNavbar />
      <div className="d-flex flex-column gap-4">
        {!hasActiveSupportedPlatforms && !demographicsLoading && (
          <LomavisCard1 title="" showDownloadButton={false}>
            <div className="text-center" role="alert">
              <div className="d-flex flex-column align-items-center">
                <i className="fa-duotone fa-solid fa-chart-simple mb-3 text-muted" style={{fontSize: 40}} ></i>
                <div className="my-10 fs-3 fw-semibold">
                  {t('allAnalytics.demographics.noSupportedPlatforms.message')}
                </div>
                <div className="d-flex flex-row gap-5 mt-10 align-items-center">
                  {SUPPORTED_PLATFORMS_DEMOGRAPHICS.map((platform) => (
                    <div key={platform}>
                      <LomavisTitle1 
                        text={t(`platforms.${platform}`)}
                        platform={platform}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </LomavisCard1>
        )}
        
        {hasActiveSupportedPlatforms && Object.keys(demographicsData).length > 0 &&
          availablePlatforms.map((platform) => {
            const platformData = extractDemographicsData(
              demographicsData,
              platform,
              t
            );

            // Only render if platform has any demographic data
            if (
              !platformData ||
              (!platformData.ageData &&
                !platformData.genderData &&
                !platformData.languageData &&
                !platformData.seniorityData &&
                !platformData.industriesData &&
                !platformData.functionsData &&
                !platformData.companySizesData &&
                !platformData.citiesData)
            ) {
              return null;
            }

            return (
              <div
                key={platform}
                ref={platformRefs[platform]}
              >
                <LomavisDemographicsSectionForPlatform1
                  title={'allAnalytics.demographics.title'}
                  platform={platform}
                  activeSupportedPlatforms={activeSupportedPlatforms}
                  {...platformData}
                />
              </div>
            );
          })}
        {hasActiveSupportedPlatforms && !demographicsLoading && Object.keys(demographicsData).length === 0 && (
          <LomavisDataProcessingPlaceholder />
        )}
        {demographicsLoading && (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ height: '200px' }}
          >
            <IsLoadingIndicator />
          </div>
        )}
      </div>
    </DemographicsRefsContext.Provider>
  );
};

export default DemographicsTab;
